import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { customValidator } from './validator';
import { CountryCodes } from '../shared/models/country-codes';
import { ServiceName, ServiceNameAr, ServiceNameEn } from '../shared/constants/services.enum';
type Part = 'cc' | 'number' | 'both';
export class Utils {
  // This method is used to get dates using selected month, selected year, mode, and is disabled
  static getDates = (
    selectedMonth: number,
    selectedYear: number,
    mode: 'past' | 'future' | 'all',
    isDisabled = false,
    noOfPastMonth: any = '',
    noOfFutureMonth: any = ''
  ) => {
    const pastMonths = Number(noOfPastMonth);
    const futureMonths = Number(noOfFutureMonth);
    const hasPastMonthLimit = !Number.isNaN(pastMonths) && pastMonths > 0;
    const hasFutureMonthLimit = !Number.isNaN(futureMonths) && futureMonths > 0;

    if (mode === 'all' && !isDisabled && (hasPastMonthLimit || hasFutureMonthLimit)) {
      const today = new Date();
      const startDate = hasPastMonthLimit
        ? new Date(today.getFullYear(), today.getMonth() - pastMonths, today.getDate())
        : new Date(1900, 0, 1);
      const endDate = hasFutureMonthLimit
        ? new Date(today.getFullYear(), today.getMonth() + futureMonths, today.getDate())
        : new Date(today.getFullYear() + 50, 11, 31);

      if (!(selectedYear && selectedMonth)) {
        return Array.from({ length: 31 }, (_, i) => {
          const day = i + 1;
          return {
            label: `${day}`.padStart(2, '0'),
            value: `${day}`.padStart(2, '0'),
          };
        });
      }

      const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
      const days = Array.from({ length: totalDays }, (_, i) => i + 1).filter((day) => {
        const candidate = new Date(Number(selectedYear), Number(selectedMonth) - 1, day);
        return candidate >= startDate && candidate <= endDate;
      });

      return days.map((day: any) => ({
        label: `${day}`.padStart(2, '0'),
        value: `${day}`.padStart(2, '0'),
      }));
    }

    if (mode === 'all') {
      return Array.from({ length: 30 }, (_, i) => {
        const day = i + 1;
        return {
          label: `${day}`.padStart(2, '0'),
          value: `${day}`.padStart(2, '0'),
        };
      });
    }

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthIndex = today.getMonth();
    const currentMonth = currentMonthIndex + 1;
    const currentDay = today.getDate();
    //Get total days in the selected month/year
    let days: any = [];
    let minDay = 1;
    let maxDay: number | null = null;

    const selectedMonthNum = Number(selectedMonth);
    const selectedYearNum = Number(selectedYear);

    if (isDisabled) {
      days = Array.from({ length: 31 }, (_, i) => i + 1);
    } else if (selectedYear && selectedMonth) {
      if (mode === 'past') {
        if (selectedYearNum === currentYear && selectedMonthNum === currentMonth) {
          maxDay = currentDay;
        }
        const pastMonths = Number(noOfPastMonth);
        if (pastMonths && !Number.isNaN(pastMonths)) {
          const startDate = new Date(currentYear, currentMonthIndex - pastMonths, currentDay);
          const startYear = startDate.getFullYear();
          const startMonth = startDate.getMonth() + 1;
          const startDay = startDate.getDate();

          const selectedMonthStart = new Date(selectedYearNum, selectedMonthNum - 1, 1);
          const rangeStart = new Date(startYear, startMonth - 1, 1);
          const rangeEnd = new Date(currentYear, currentMonthIndex, 1);
          if (selectedMonthStart < rangeStart || selectedMonthStart > rangeEnd) {
            return [];
          }

          if (selectedYearNum === startYear && selectedMonthNum === startMonth) {
            minDay = startDay;
          }
        }
      } else if (mode === 'future') {
        if (selectedYearNum === currentYear && selectedMonthNum === currentMonth) {
          minDay = currentDay + 1;
        }
      }

      const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
      //Filter days based on mode
      const upperBound = maxDay ? Math.min(maxDay, totalDays) : totalDays;
      days = Array.from({ length: totalDays }, (_, i) => i + 1).filter((day) => {
        return day >= minDay && day <= upperBound;
      });
    } else {
      days = Array.from({ length: 30 }, (_, i) => i + 1).filter((day) => {
        if (mode === 'past') return day <= currentDay;

        return true;
      });
    }

    return days.map((day: any) => ({
      label: `${day}`.padStart(2, '0'),
      value: `${day}`.padStart(2, '0'),
    }));
  };

  // This method is used to get months using no of past month, selected year, mode, and is disabled
  static getMonths = (
    noOfPastMonth: any,
    selectedYear: any,
    mode: 'past' | 'future' | 'all',
    isDisabled = false,
    noOfFutureMonth: any = ''
  ) => {
    let monthNames = [
      { value: '01', en: '01', ar: '01' },
      { value: '02', en: '02', ar: '02' },
      { value: '03', en: '03', ar: '03' },
      { value: '04', en: '04', ar: '04' },
      { value: '05', en: '05', ar: '05' },
      { value: '06', en: '06', ar: '06' },
      { value: '07', en: '07', ar: '07' },
      { value: '08', en: '08', ar: '08' },
      { value: '09', en: '09', ar: '09' },
      { value: '10', en: '10', ar: '10' },
      { value: '11', en: '11', ar: '11' },
      { value: '12', en: '12', ar: '12' },
    ];
    let lang = 'en';
    const pastMonths = Number(noOfPastMonth);
    const futureMonths = Number(noOfFutureMonth);
    const hasPastMonthLimit = !Number.isNaN(pastMonths) && pastMonths > 0;
    const hasFutureMonthLimit = !Number.isNaN(futureMonths) && futureMonths > 0;

    if (mode === 'all' && !isDisabled && (hasPastMonthLimit || hasFutureMonthLimit)) {
      const today = new Date();
      const startDate = hasPastMonthLimit
        ? new Date(today.getFullYear(), today.getMonth() - pastMonths, 1)
        : new Date(1900, 0, 1);
      const endDate = hasFutureMonthLimit
        ? new Date(today.getFullYear(), today.getMonth() + futureMonths, 1)
        : new Date(today.getFullYear() + 50, 11, 1);
      const resolvedSelectedYear =
        selectedYear === null || selectedYear === undefined || selectedYear === ''
          ? today.getFullYear()
          : Number(selectedYear);

      const monthsToShow = monthNames.filter((month: any) => {
        const monthNum = parseInt(month.value, 10);
        const candidate = new Date(resolvedSelectedYear, monthNum - 1, 1);
        return candidate >= new Date(startDate.getFullYear(), startDate.getMonth(), 1)
          && candidate <= new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      });

      return monthsToShow.map((m: any) => ({ label: m[lang], value: m.value }));
    }

    if (mode === 'all') {
      return monthNames.map((m: any) => ({ label: m[lang], value: m.value }));
    }

    if (isDisabled) {
      return monthNames.map((m: any) => ({ label: m[lang], value: m.value }));
    }
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth is zero-based
    let monthsToShow = monthNames;
    const normalizedSelectedYear =
      selectedYear === null || selectedYear === undefined || selectedYear === ''
        ? null
        : Number(selectedYear);

    if (mode === 'past') {
      const resolvedSelectedYear =
        normalizedSelectedYear === null ? currentYear : normalizedSelectedYear;
      if (noOfPastMonth === '') {
        if (resolvedSelectedYear === currentYear) {
          return monthNames
            .filter((month: any) => parseInt(month.value, 10) <= currentMonth)
            .map((month: any) => ({
              label: month[lang],
              value: month.value,
            }));
        }
        return monthNames.map((month: any) => ({
          label: month[lang],
          value: month.value,
        }));
      }
      let startMonth = 1;
      let startYear = currentYear;
      if (noOfPastMonth) {
        const startDate = new Date(currentYear, currentMonth - 1 - noOfPastMonth, 1);
        startMonth = startDate.getMonth() + 1;
        startYear = startDate.getFullYear();
      }

      if (resolvedSelectedYear === currentYear) {
        const minMonth = resolvedSelectedYear === startYear ? startMonth : 1;
        monthsToShow = monthNames.filter((month: any) => {
          const monthNum = parseInt(month.value, 10);
          return monthNum >= minMonth && monthNum <= currentMonth;
        });
      } else if (resolvedSelectedYear === startYear) {
        monthsToShow = monthNames.filter((month: any) => {
          const monthNum = parseInt(month.value, 10);
          return monthNum >= startMonth;
        });
      } else if (
        resolvedSelectedYear < startYear ||
        resolvedSelectedYear > currentYear
      ) {
        monthsToShow = [];
      }
    } else if (normalizedSelectedYear === currentYear && mode === 'future') {
      monthsToShow = monthNames.filter((month: any) => {
        const monthNum = parseInt(month.value, 10);
        return monthNum >= currentMonth;
      });
    }

    return monthsToShow.map((month: any) => ({
      label: month[lang],
      value: month.value,
    }));
  };

  // This method is used to get years using number past years, mode, and is disabled
  static getYears = (
    numberPastYears: any,
    mode: 'past' | 'future' | 'all',
    isDisabled = false,
    disabledStartYear: number = 1900,
    futureYearCount?: number | null,
    noOfPastMonth: any = '',
    noOfFutureMonth: any = ''
  ) => {
    const currentYear = new Date().getFullYear();
    const pastMonths = Number(noOfPastMonth);
    const futureMonths = Number(noOfFutureMonth);
    const hasPastMonthLimit = !Number.isNaN(pastMonths) && pastMonths > 0;
    const hasFutureMonthLimit = !Number.isNaN(futureMonths) && futureMonths > 0;

    if (mode === 'all' && !isDisabled && (hasPastMonthLimit || hasFutureMonthLimit)) {
      const today = new Date();
      const startYear = hasPastMonthLimit
        ? new Date(today.getFullYear(), today.getMonth() - pastMonths, 1).getFullYear()
        : 1900;
      const endYear = hasFutureMonthLimit
        ? new Date(today.getFullYear(), today.getMonth() + futureMonths, 1).getFullYear()
        : currentYear + 50;

      return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
        const year = endYear - i;
        return { label: `${year}`, value: year };
      });
    }

    if (mode === 'all') {
      const startYear = 1900;
      const endYear = currentYear + 50;
      return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
        const year = startYear + i;
        return { label: `${year}`, value: year };
      });
    }

    let noOfYears = mode == 'past' ? 70 : 20;
    if (mode === 'future' && futureYearCount != null && !Number.isNaN(Number(futureYearCount))) {
      noOfYears = Number(futureYearCount);
    }

    if (isDisabled) {
      // Full range: 1900 → +50 years future
      const startYear = disabledStartYear;
      const endYear = currentYear + 50;
      return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
        const year = startYear + i;
        return { label: `${year}`, value: year };
      });
    }

    return Array.from(
      { length: numberPastYears ? numberPastYears : noOfYears },
      (_, i) => {
        const year = mode === 'future' ? currentYear + i : currentYear - i;
        return { label: `${year}`, value: year };
      }
    );
  };

  // function for check form invalid fields
  // This method is used to get invalid controls using form
  static getInvalidControls(form: FormGroup | FormArray): string[] {
    const invalid: string[] = [];

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control instanceof FormGroup || control instanceof FormArray) {
        const childInvalid = this.getInvalidControls(control);
        invalid.push(...childInvalid.map((name) => `${key}.${name}`));
      } else if (control && control.invalid) {
        invalid.push(key);
      }
    });

    return invalid;
  }

  // This method is used to check if any control has maxlength error
  static hasMaxLengthError(control: AbstractControl | null | undefined): boolean {
    if (!control) {
      return false;
    }
    if (control?.errors?.['maxlength']) {
      return true;
    }

    if (control instanceof FormGroup) {
      return Object.keys(control.controls).some((key) =>
        Utils.hasMaxLengthError(control.controls[key])
      );
    }

    if (control instanceof FormArray) {
      return control.controls.some((child) => Utils.hasMaxLengthError(child));
    }

    return false;
  }
  // Keep the original (non-required) validators per-control to avoid collisions between different forms
  static originalValidatorsMap = new WeakMap<AbstractControl, ValidatorFn[]>();
  // This method is used to add remove validation using form, condition, and controls
  static addRemoveValidation(
    form: FormGroup,
    condition: boolean,
    controls: string[]
  ): void {
    controls.forEach((key) => {
      const ctl = form.get(key)!;

      // Store original validators (without required) the first time
      if (!this.originalValidatorsMap.has(ctl)) {
        const currentValidators = ctl.validator
          ? [ctl.validator as ValidatorFn]
          : [];
        this.originalValidatorsMap.set(ctl, currentValidators);
      }
      const baseValidators = this.originalValidatorsMap.get(ctl) || [];

      if (condition) {
        // Add required along with original validators
        ctl.setValidators([Validators.required, ...baseValidators]);
      } else {
        // Restore only the original validators (no required)
        ctl.setValidators(baseValidators);
        ctl.setValue(null, { emitEvent: false });
      }

      ctl.updateValueAndValidity({ emitEvent: false });
    });
  }

  // This method is used to check is array using key
  static checkIsArray(key: any) {
    return Array.isArray(key) ? key : key ? [key] : [];
  }

  // This method is used to generate unique id
  private static counter = 0;
  private static lastTimestamp = "";
  static generateUniqueId(): string {
    const now = new Date();
    const pad = (n: number, size: number = 2) => n.toString().padStart(size, '0');
    const timestamp =
      pad(now.getFullYear() % 100, 2) + // YY
      pad(now.getMonth() + 1) +        // MM
      pad(now.getDate()) +             // DD
      pad(now.getHours()) +            // HH
      pad(now.getMinutes()) +          // MM
      pad(now.getSeconds());           // SS
    // → 12 digits

    // Reset counter when second changes
    if (timestamp !== Utils.lastTimestamp) {
      Utils.lastTimestamp = timestamp;
      Utils.counter = 0;
    }
    // 2-digit or 3-digit counter
    const counterPart = pad(Utils.counter++, 2); // 00–99
    // Total length = 14 digits
    return timestamp + counterPart;
  }

  // This method is used to calculate form progress using completed steps and total steps
  static calculateFormProgress(
    completedSteps: number,
    totalSteps: number
  ): number {
    if (totalSteps <= 0 || completedSteps <= 1) return 0;
    const percentage = ((completedSteps - 1) / totalSteps) * 100;
    return Math.round(percentage);
  }

  // This method is used to handle invalid step using form, all fields, and step fields
  static handleInvalidStep(
    form: FormGroup,
    allFields: any = [],
    stepFields: string[]
  ): boolean {
    if (
      stepFields.length > 0 &&
      stepFields.some((field) => form.get(field)?.invalid)
    ) {
      // Mark fields as touched
      stepFields.forEach((field) => form.get(field)?.markAsTouched());
      // Focus first invalid control
      Utils.focusFirstInvalidControl(form, allFields, stepFields);
      return true; // Form has invalid fields
    }
    return false; // No invalid fields
  }

  // This method is used to focus first invalid control using form, all fields, and step fields
  static focusFirstInvalidControl(
    form: FormGroup,
    allFields: any[] = [],
    stepFields: string[] = []
  ): void {
    let matchingFieldComponent: any;
    if (stepFields && stepFields.length > 0) {
      const firstInvalid = stepFields.find((field) => form.get(field)?.invalid);
      if (!firstInvalid) return;
      matchingFieldComponent = allFields.find(
        (comp) => comp.control === form.get(firstInvalid)
      );
    } else {
      matchingFieldComponent = allFields.find(
        (field: any) => field.control?.invalid
      );
    }

    if (matchingFieldComponent) {
      matchingFieldComponent.focus();
    }
  }

  // This method is used to parse mobile using raw and part
  static parseMobile(raw: string, part: Part = 'both') {
    let countryCodes = CountryCodes.LIST;
    let knownCountryCodes: any = [];
    countryCodes.map((c) => {
      knownCountryCodes.push(c.code);
    });

    const clean = raw.replace(/[^\d]/g, '');
    // Try matching 1 to 3 digit country code from the start
    let cc = '';
    for (let len = 1; len <= 3; len++) {
      const code = clean.slice(0, len);
      if (knownCountryCodes.includes(code)) {
        cc = code;
        break;
      }
    }

    const number = cc ? clean.slice(cc.length) : clean;

    switch (part) {
      case 'cc':
        return cc;
      case 'number':
        return number;
      default:
        return [cc, number]; // ["91", "8548862507"]
    }
  }
  // This method is used to to capitalize using text
  static toCapitalize(text: string) {
    return text[0].toUpperCase() + text.slice(1).toLowerCase();
  }

  // This method is used to disable fields if amendment or renew using type and form
  static disableFieldsIfAmendmentOrRenew(type: string, form: FormGroup): void {
    let controlsToDisable = [
      'licenseIssuingAuthority',
      'licenceNumber',
      'issueDate.day',
      'issueDate.month',
      'issueDate.year',
      'expiryDate.day',
      'expiryDate.month',
      'expiryDate.year',
    ];
    if (type === 'amendment') {
      controlsToDisable.push('membershipYear', 'expiryDate.day',
        'expiryDate.month',
        'expiryDate.year',);
      controlsToDisable.forEach((path) => form.get(path)?.disable());
    }
    if (type === 'renew') {
      controlsToDisable.forEach((path) => form.get(path)?.disable());
    }
  }

  // This method is used to disable fields for replace using type and form
  static disableFieldsForReplace(type: string, form: FormGroup): void {
    let controlsToDisable = [
      // "carnetNumber",
      'holderType',
      'contact.fullName',
      'contact.email',
      'contact.mobile',
      'contact.countryCode',
      'contact.phone',
      'representative.fullName',
      'representative.email',
      'representative.mobile',
      'representative.countryCode',
      'representative.phone',
      'company.name',
      'company.mobile',
      'company.countryCode',
      'company.phone',
      'cargo.purpose',
      'cargo.currency',
      'products',
      'carnetType',
      'journeyDetails',
      'journeyDestination',
      'journeyEntryType',
      'journeyTransportMode',
      'journeyAddCounterfoils',
      'visitCounterfoil',
      'transitCounterfoil',
      'company.hasOtherEmirateLicense',
      'company.otherEmirateName'
    ];

    if (type == ServiceName.AMENDMENT_CARNET) {
      let amendFieldsToDisable = ['cargo.currency', 'carnetType'];
      amendFieldsToDisable.forEach((control) => form.get(control)?.disable());
    }

    if (type == ServiceName.REPLACE_CARNET) {
      controlsToDisable.push('processingType');
      controlsToDisable.forEach((control) => form.get(control)?.disable());
    }
    if (type == ServiceName.SUBSTITUTE_CARNET) {
      controlsToDisable.push('processingType');
      controlsToDisable.forEach((control) => form.get(control)?.disable());
    }
  }
  // This method is used to disable fields based on sub service using type and form
  static disableFieldsBasedOnSubService(
    type: string,
    form: FormGroup,
    cooPrintDate?: string | Date | null
  ): void {
    let controlsToDisable = [
      'cooNumber',
      'exporter',
      'consignee',
      'invoiceNo',
      'invoiceDate.day',
      'invoiceDate.month',
      'invoiceDate.year',
      'invCurrency',
      'invAmount',
      'transportMode',
      'countryDest',
      'stampPosition',
      'products',
    ];
    if (type === 'amendment-coo') {
      //Removed this condiation as per puneet on 22/07/2026
      //const isRecentPrintDate = Utils.isWithinLastTwoMonths(cooPrintDate);
      //if (isRecentPrintDate) {
        controlsToDisable.forEach((control) => form.get(control)?.enable());
        form.get('cooNumber')?.disable();
        form.get('invoiceNo')?.disable();
        form.get('countryDest')?.disable();
        return;
      //}
      //controlsToDisable.forEach((control) => form.get(control)?.disable());
    }
  }

  // Method for check coo print date within two moths
  private static isWithinLastTwoMonths(dateValue?: string | Date | null): boolean {
    if (!dateValue) {
      return false;
    }
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    const now = new Date();
    const cutoff = new Date(now.getTime());
    cutoff.setMonth(cutoff.getMonth() - 2);
    return date >= cutoff;
  }
  // This method is used to set control validity based on document using form, field path, and document
  static setControlValidityBasedOnDocument(
    form: FormGroup,
    fieldPath: string,
    document?: any[]
  ): void {
    const control = form.get(fieldPath);

    if (!control) return;

    if (document && document.length > 0) {
      control.setValue(true);
      control.setErrors({ required: false });
    } else {
      // Check if 'required' is part of the validator
      const validatorFn = control.validator;
      if (validatorFn) {
        const validationResult = validatorFn({} as AbstractControl);
        if (validationResult?.['required']) {
          control.setErrors({ required: true });
        } else {
          control.setErrors(null);
        }
      }
    }

    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  // This method is used to get invalid field messages using invalid controls and control labels
  static getInvalidFieldMessages(
    invalidControls: string[],
    controlLabels: Record<string, string>
  ): string[] {
    return invalidControls.map((ctrl) => {
      const label = controlLabels[ctrl] || ctrl;
      return `The field "${label}" is invalid. Please enter a correct value.`;
    });
  }
  // This method is used to get dubai date time
  static getDubaiDateTime(): string {
    const now = new Date();
    // Convert to Dubai timezone by adding 4 hours (GMT+4)
    const dubaiOffset = 4 * 60; // 4 hours in minutes
    const localOffset = now.getTimezoneOffset(); // in minutes
    const dubaiTime = new Date(
      now.getTime() + (dubaiOffset + localOffset) * 60000
    );

    const yyyy = dubaiTime.getFullYear();
    const MM = String(dubaiTime.getMonth() + 1).padStart(2, '0');
    const dd = String(dubaiTime.getDate()).padStart(2, '0');
    const HH = String(dubaiTime.getHours()).padStart(2, '0');
    const mm = String(dubaiTime.getMinutes()).padStart(2, '0');

    return `${yyyy}${MM}${dd}${HH}${mm}`;
  }

  // This method is used to at least one selected validator using control
  static atLeastOneSelectedValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const formArray = control as any;
    return formArray.controls.some((ctrl: AbstractControl) => ctrl.value)
      ? null
      : { required: true }; // if none checked
  }

  // This method is used to get advance payment balance using payments
  static getAdvancePaymentBalance(payments: any[]): number {
    const profiles = Utils.checkIsArray(payments);
    if (profiles && profiles.length > 0) {
      const filtered = profiles.filter(
        (p) => p['DC Advance For Services Balance'] !== ''
      );
      if (filtered.length > 0) {
        return filtered.reduce((sum, p) => {
          const amount = parseFloat(p['DC Advance For Services Balance']) || 0;
          return sum + amount;
        }, 0);
      }
    }
    return 0; // default if none
  }
  // Utility method to decode and stringify
  // This method is used to decode and stringify using response
  static decodeAndStringify(response: Record<string, any>): any {
    const decoded: Record<string, any> = {};

    for (const key in response) {
      if (Object.prototype.hasOwnProperty.call(response, key)) {
        const value = response[key];
        decoded[key] =
          typeof value === 'string' ? decodeURIComponent(value) : value;
      }
    }

    return decoded; // return as plain object
  }
  static extractFileNameFromUrl(fileUrl: string): string {
    if (!fileUrl) {
      return '';
    }

    try {
      const url = new URL(fileUrl, window.location.origin);
      const pathSegments = url.pathname.split('/').filter(segment => segment);
      const fileName = pathSegments[pathSegments.length - 1] || '';
      return decodeURIComponent(fileName);
    } catch {
      const segments = fileUrl.split('/').filter(segment => segment);
      const fallbackFileName = segments[segments.length - 1] || '';
      return decodeURIComponent(fallbackFileName);
    }
  }
  static checkIsValidURL(url: unknown) {
    if (typeof url !== 'string') {
      return false;
    }

    const trimmed = url.trim();
    if (!trimmed) {
      return false;
    }

    try {
      const parsed = new URL(trimmed);
      return parsed.protocol === 'https:' && !!parsed.hostname;
    } catch {
      return false;
    }
  }

  //this method is use to get en and arbic value for service type
  static getServiceLabels(subType: string, lang: 'en' | 'ar'): { service: string; serviceAr: string } {
    // Find the enum key for the given subType (it could be English or Arabic)
    const matchingKey = Object.keys(ServiceNameEn).find(
      key =>
        ServiceNameEn[key as keyof typeof ServiceNameEn] === subType ||
        ServiceNameAr[key as keyof typeof ServiceNameAr] === subType
    ) as keyof typeof ServiceNameEn | undefined;

    if (!matchingKey) {
      // fallback if no match found
      return { service: subType, serviceAr: subType };
    }

    const serviceLabel = ServiceNameEn[matchingKey];
    const serviceArLabel = ServiceNameAr[matchingKey] || serviceLabel;

    return {
      // service should always be English version
      service: serviceLabel,
      // Arabic label should always return the Arabic translation
      serviceAr: serviceArLabel,
    };
  }

  /**
 * Checks whether a membership should be shown based on expiry date.
 * Rule: show ONLY if (expiryDate + 30 days) > today.
 *
 * @param expiryDate - date in MM/DD/YYYY format
 * @returns true if membership is valid, false otherwise
 */
  static isMembershipValid(expiryDate: string): boolean {
    if (!expiryDate) return false;

    const [month, day, year] = expiryDate.split('/').map(Number);
    const expDate = new Date(year, month - 1, day);

    expDate.setDate(expDate.getDate() + 30);

    const today = new Date();

    // Remove time part
    expDate.setHours(23, 59, 59, 999);

    return expDate >= today;
  }

  // This method is used to parse expiry date to Date object
  static parseExpiryDate(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value);
    }

    const raw = String(value ?? '').trim();
    if (!raw) {
      return null;
    }

    const parts = raw.split('/');
    if (parts.length === 3) {
      const month = Number(parts[0]);
      const day = Number(parts[1]);
      const year = Number(parts[2]);
      if (
        Number.isFinite(month) &&
        Number.isFinite(day) &&
        Number.isFinite(year) &&
        month >= 1 && month <= 12 &&
        day >= 1 && day <= 31
      ) {
        return new Date(year, month - 1, day);
      }
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  // This method is used to get status display label using lov options
  static getStatusLabel(serviceStatus: string, statusOptions: any[]): string {
    if (!serviceStatus) {
      return '';
    }
    const normalized = String(serviceStatus).trim().toLowerCase();
    const match = (Array.isArray(statusOptions) ? statusOptions : []).find(
      (option: any) => String(option?.Name ?? '').trim().toLowerCase() === normalized ||
        String(option?.Value ?? '').trim().toLowerCase() === normalized
    );
    const label = match?.Value ?? match?.Name;
    return label ? String(label) : serviceStatus;
  }

  // Changes for attestation sub services display name/value mapping
  static
  getSrTypeValue(srType: string, srSubType?: string){
     if (srType && srType == ServiceNameEn.SIGNATURE_OF_VERIFICATION && !srSubType) {
      return ServiceName.ATTESTATION;
    }
    if (srSubType && srType == ServiceNameEn.SIGNATURE_OF_VERIFICATION) {
      if (srSubType == ServiceNameEn.ATTESTATION_AUTH_CERTIFICATE) {
        return ServiceName.ATTESTATION_AUTH_CERTIFICATE_DISPLAY;
      }
      if (srSubType == ServiceNameEn.ATTESTATION_CERTIFY_DOCUMENT) {
        return ServiceName.ATTESTATION_CERTIFY_DOCUMENT_DISPLAY;
      }
      if (srSubType == ServiceNameEn.ATTESTATION_VERIFY_SIGNATURE) {
        return ServiceName.ATTESTATION_VERIFY_SIGNATURE_DISPLAY;
      }
    }
    return (srType && srSubType) ? srSubType : srType;
  }
}
